
import React, { useState } from 'react';
import {
    ScrollablePane,
    Stack,
    IStackStyles,
    TextField,
    ITextFieldStyles,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    DefaultButton } from "@fluentui/react";
import { useBoolean } from '@fluentui/react-hooks';
import { useMsal } from '@azure/msal-react';
import { AccessRequestResult } from '../../DataModels/AccessRequestResult';
import { IAMService } from '../../Services/IAMService';
import { IGenericDialogTemplate } from '../../DataModels/GenericDialogTemplate';
import { GenericResponseError } from '../../DataModels/Errors/GenericResponseError';
import { ActionConfirmationDialog } from '../../Components/ActionConfirmationDialog';
import * as SiteStyle from '../Styles';
import { AdminPortalFooter } from '../../AdminPortalFooter';
import { IAppInsightsLogger } from '../../Utilities/IAppInsightsLogger';
import { CreateUserDto } from '../../DataModels/CreateUserDto';
import { GenericHelper } from '../../Utilities/GenericHelper';

const tokens = {
    stackTokens: {
      childrenGap: 20,
      maxHeight: 100,
    },
    spinnerStack: {
      childrenGap: 20,
    },
  };
const stackStyles: Partial<IStackStyles> = {
    root: {
        justifyContent: 'left', alignItems: 'left', paddingLeft: 10, paddingRight: 10, width: 320,
        selectors: {
            '@media screen and (min-resolution: 2dppx) and (max-width: 600px)': {
                width: '80%'
            },
        }
    }
};
const buttonStackStyles: Partial<IStackStyles> = {
    root: {
        paddingBottom: 40, marginLeft: 0,
        selectors: {
            '@media screen and (min-resolution: 2dppx) and (max-width: 600px)': {
                minWidth: '40%',
                width: '100%'
            },
        }
    }
};
const inputBoxStyle: Partial<ITextFieldStyles> = {
    fieldGroup: {
        minWidth: '80px',
        selectors: {
            '@media screen and (min-resolution: 2dppx) and (max-width: 600px)': {
                minWidth: '40%',
                width:'100%'
            },
        },
    },
};

interface ICreateUserProfilePageProps {
    iamService: IAMService
    logger: IAppInsightsLogger;
}


export function CreateUserProfilePage(props: ICreateUserProfilePageProps) {
    const { accounts } = useMsal();
    const loggedInUserEmail = accounts[0]?.username ?? '';

    const [shouldShowIndicator, setShowIndicator] = useState(false);
    const [submitButtonDisabled, setDisableSubmitButton] = useState(true);
    const [disableInputFields, setDisableInputField] = useState(false);
    const [resetButtonClicked, setResetButtonClicked] = useState(false);

    const [showDialog, {toggle: toggleHideDialog}] = useBoolean(true);
    const [dialogMessage, setDialogMessage] = useState<IGenericDialogTemplate>();


    var createUserDto: CreateUserDto = {
        user : {
            email : '',
            firstName :'',
            lastName : '',
            jobTitle : '',
            organization:''
        },
        businessJustification : '',
        microsoftBusinessContact : '',
        notifyUser : false,
        organizationId : '',
        requestedBy: loggedInUserEmail,
    };

    const [createUser, setCreateUser] = useState<CreateUserDto>(createUserDto);

      const emailValidation = (value: string, controlName: string) => {
        if(value.length === 0)
        {
            return '';
        }

        if(controlName === 'Microsoft Buisness Contact' && (!GenericHelper.validateEmail(value) ||  !value.endsWith('@microsoft.com') ))
        {
            return 'A valid Microsoft Email is required. E.g. "user@microsoft.com".';
        }

        if(!GenericHelper.validateEmail(value))
        {
            return 'A valid email address is required. E.g. joe@contoso.com';
        }
        return '';
      };

    const _onResetClicked = () => {
        setCreateUser({
            user : {
                email : '',
                firstName :'',
                lastName : '',
                jobTitle : '',
                organization: '',
            },
            businessJustification : '',
            microsoftBusinessContact : '',
            notifyUser : false,
            organizationId : '',
            requestedBy: loggedInUserEmail,
        });
        setResetButtonClicked(true);
        setDisableSubmitButton(true);
        setTimeout(() => {
            setResetButtonClicked(false);
         }, 2000);        
    }

    const _onSubmitClicked = () => {
         setShowIndicator(true);
         setDisableSubmitButton(true);
         setDisableInputField(true);
         
        props.iamService.onboardUser({...createUser})
            .then((result: AccessRequestResult) => {
                setShowIndicator(false);
                setDisableInputField(false);
                setDisableSubmitButton(true);

                let dialogTitle = 'Create User';
                let dialogBody = 'User is successfully onboarded.';

                if (result.status === 'AllCreated') {
                    dialogTitle = 'Create User';
                    dialogBody = 'User is successfully onboarded.';
                } else if (result.status === 'AlreadyGranted') {
                    dialogTitle = 'Access Already Granted';
                    const roleMessages = result.notSubmittedRoles
                        ?.map((r) => `${r.name}: ${r.message}`)
                        .join('\n') || 'User already has an active access grant.';
                    dialogBody = roleMessages;
                } else if (result.status === 'RequestInReview') {
                    dialogTitle = 'Request Already In Review';
                    const roleMessages = result.notSubmittedRoles
                        ?.map((r) => `${r.name}: ${r.message}`)
                        .join('\n') || 'A request for this user is already pending approval.';
                    dialogBody = roleMessages;
                } else if (result.status === 'AllFailed') {
                    dialogTitle = 'Failed To Onboard User';
                    const roleMessages = result.notSubmittedRoles
                        ?.map((r) => `${r.name}: ${r.message}`)
                        .join('\n') || 'All role assignments failed.';
                    dialogBody = roleMessages;
                } else {
                    dialogTitle = 'Request Not Submitted';
                    dialogBody = result.notSubmittedRoles
                        ?.map((r) => `${r.name}: ${r.message}`)
                        .join('\n') || `Failed to onboard user. Status: ${result.status}`;
                }

                setDialogMessage({
                    title: dialogTitle,
                    actionType: 'Create User',
                    messageBody: dialogBody,
                    isError: result.status !== 'AllCreated'
                });

                toggleHideDialog();
                setCreateUser({
                    user : {
                        email : '',
                        firstName :'',
                        lastName : '',
                        jobTitle : '',
                        organization: '',
                    },
                    businessJustification : '',
                    microsoftBusinessContact : '',
                    notifyUser : false,
                    organizationId : '',
                    requestedBy: '',
                });
            })
            .catch(error => {
                setShowIndicator(false);
                setDisableInputField(false);
                var errorMessage = ``;
                if (error instanceof GenericResponseError) {
                    var responseError = error as GenericResponseError;
                    errorMessage = `Failed to add user. ${responseError.message}. Error status code: ${responseError.code}. Correlation Id: ${responseError.correlationId}.`
                }
                else {
                    errorMessage = `Failed to add user. ${error.message}.`
                }

                setDialogMessage({
                    title: `Failed To Add User`,
                    actionType: 'Create User',
                    messageBody: errorMessage,
                    isError: true
                });

                toggleHideDialog();
                setDisableSubmitButton(false);
            });
    }

    const handleChange = (event: any, controlName:string) => 
    {
            var updatedObject:CreateUserDto = {...createUser} ;  
            switch(controlName)
            {
                case 'Email' : 
                {
                    updatedObject = {...createUser, user: {...createUser.user, email:event.target.value}}
                }
                break;
                case 'FirstName' : 
                {
                    updatedObject = {...createUser, user: {...createUser.user, firstName:event.target.value}}
                }
                break;
                case 'LastName' : 
                {
                    updatedObject = {...createUser, user: {...createUser.user, lastName:event.target.value}}
                }
                break;
                case 'JobTitle' : 
                {
                    updatedObject = {...createUser, user: {...createUser.user, jobTitle:event.target.value}}
                }
                break;
                case 'Microsoft Buisness Contact' : 
                {
                    updatedObject = {...createUser,  microsoftBusinessContact: event.target.value}
                }
                break;
                case 'Organization' : 
                {
                    updatedObject = {...createUser, user: {...createUser.user, organization:event.target.value}}
                }
                break;
                case 'BusinessJustification' : 
                {
                    updatedObject = {...createUser, businessJustification:event.target.value}
                }
                break;
            }
            setCreateUser(updatedObject);
            if(updatedObject.user.email.length > 0
                && emailValidation(updatedObject.user.email,'Email') === '' 
                && updatedObject.user.firstName.trim().length > 0
                 && updatedObject.user.lastName.trim().length > 0 
                 && updatedObject.user.jobTitle.trim().length > 0
                 && updatedObject.user.organization.trim().length > 0
                 && updatedObject.businessJustification.trim().length > 0
                 && updatedObject.microsoftBusinessContact.length > 0
                 && emailValidation(updatedObject.microsoftBusinessContact,'Microsoft Buisness Contact') === '' )
                 {
                    setDisableSubmitButton(false);
                 }
                 else
                 {
                     setDisableSubmitButton(true);
                 }
    }

    return (
        <>
            <ScrollablePane>
                <Stack tokens={tokens.stackTokens} className={SiteStyle._Styles.mainAreaStyle} >
                    <h1 role='heading'>Create User</h1>
                    <h2 role='heading'>Add user to existing organization</h2>
                    <Stack wrap tokens={tokens.stackTokens}  styles={stackStyles}>
                            <TextField styles={inputBoxStyle} required disabled={disableInputFields}  value={createUser.user.email} onChange={event => handleChange(event,'Email')}  label="Email" type='email' data-testid="txtUserEmail" placeholder="username@contoso.com" maxLength={320} onGetErrorMessage={(value:string) => {return emailValidation(value,"Email");}} validateOnFocusOut />
                            <TextField styles={inputBoxStyle} required label="First Name" data-testid="txtUserFirstName" disabled={disableInputFields}  value={createUser.user.firstName} onChange={event => handleChange(event,'FirstName')} placeholder="Your first name" maxLength={100} />
                            <TextField styles={inputBoxStyle} required label="Last Name" data-testid="txtLastName" disabled={disableInputFields}  value={createUser.user.lastName} onChange={event => handleChange(event,'LastName')} placeholder="Your last name" maxLength={100} />
                            <TextField styles={inputBoxStyle} required label="Organization" data-testid="txtOrganization" disabled={disableInputFields} value={createUser.user.organization} onChange={event => handleChange(event,'Organization')} placeholder="Organization name" maxLength={100} />
                            <TextField styles={inputBoxStyle} required label="Job Title" data-testid="txtJobTitle" disabled={disableInputFields}  value={createUser.user.jobTitle} onChange={event => handleChange(event,'JobTitle')} placeholder="Job Title e.g. Product Manager, Project Manager, Engineer" maxLength={50} />
                            <TextField styles={inputBoxStyle} required label="Microsoft Business Contact" disabled={disableInputFields}  value={createUser.microsoftBusinessContact} onChange={event => handleChange(event,'Microsoft Buisness Contact')} data-testid="txtmsftcontact" placeholder="Email of Microsoft contact, ending with @microsoft.com" maxLength={320} onGetErrorMessage={(value:string) => {return emailValidation(value,"Microsoft Buisness Contact");}} validateOnFocusOut />
                            <TextField  styles={inputBoxStyle} required label="Business Justification" value={createUser.businessJustification} disabled={disableInputFields}  onChange={event => handleChange(event,'BusinessJustification')} data-testid="txtBusinessJustification" placeholder="Reason for your request today" multiline rows={3} maxLength={200} />
                            <TextField styles={inputBoxStyle} label="Requested By" type='email' readOnly autoComplete="off" disabled={disableInputFields} value={createUser.requestedBy} data-testid="txtRequestedBy" maxLength={320} />
                     <Stack horizontal tokens={tokens.stackTokens} styles={buttonStackStyles} >
                        <PrimaryButton text='Create' data-testid="btnCreateUser" onClick={_onSubmitClicked} disabled={submitButtonDisabled}></PrimaryButton>
                        <DefaultButton text='Reset' type='reset' onClick={_onResetClicked} ariaDescription="Clear the fields" data-testid="btnReset"/>
                        {
                            resetButtonClicked && 
                                <div id="alertResetMessage" role="alert" aria-label='Reset button pressed. Your previous selections have been cleared.'></div>
                        }
                        {
                                    shouldShowIndicator &&
                                        <Spinner size={SpinnerSize.medium} labelPosition='right'/>
                        }
                    </Stack>
                    
                    {
                        <div>
                            <AdminPortalFooter />
                        </div>
                    }
                </Stack>
                </Stack>
            </ScrollablePane>
            {
                dialogMessage &&
                    <ActionConfirmationDialog
                        dialogData={dialogMessage}
                        showDialog={showDialog}
                        onDismiss={toggleHideDialog}
                        data-testid="dialogBox"
                    />
            }
        </>
    );
}